// 商户侧栏角标 hook（FIX-008 / FIX-009）：
// 「通道充值」「快捷处置」去掉 menu 硬编码，按真实待办计数。
// 口径：
//   - 通道充值：listPositionTopups → 进行中状态
//     PENDING_PAYMENT / DETECTING / PENDING_REVIEW（与页内「待付款/链上检测中/待人工复核」同源）
//   - 快捷处置：listQuickActions 返回的是处置历史，不是待办队列；无待办则 0（不显示角标）
// 审批中心（PAGE-MCH-USER-015 / AC-PAGE-MCH-USER-015-04）：
//   - approvalCenterPendingCount = APC-002 五类型 pending 总数（动态计数，禁写死）；
//     失败/无权限 → null 隐藏角标；决策成功/手动刷新经 APPROVALS_BADGE_EVENT 即时重拉
import { useEffect, useState } from 'react';
import { loadSession } from '@/api/client';
import { listPositionTopups } from '@/api/positions';
import { listQuickActions } from '@/api/risk';
import { fetchNoticeUnreadCount } from '@/api/notices';
import { fetchPendingApprovalCount } from '@/api/approvals';
import { approvalBadgeCount, APPROVALS_BADGE_EVENT } from '@/api/approvalsCore';
import { NOTICE_BADGE_EVENT } from '@/hooks/useUnreadNoticeCount';
import { canReadQuickActions, merchantPermissionsOf } from '@/lib/hasMerchantPermission';

const TOPUP_INFLIGHT = new Set(['PENDING_PAYMENT', 'DETECTING', 'PENDING_REVIEW']);

export type MerchantBadges = {
  topupInflightCount: number | null;
  quickActionPendingCount: number | null;
  /** 消息中心未读数（需求9 · D3 口径全类型未读总数；失败为 null 隐藏角标） */
  unreadNoticeCount: number | null;
  /** 审批中心五类型 pending 总数（APC-002 动态计数；失败为 null 隐藏角标，禁写死） */
  approvalCenterPendingCount: number | null;
};

export function useMerchantBadges(pathname: string): MerchantBadges {
  const [topupInflightCount, setTopupInflightCount] = useState<number | null>(null);
  const [quickActionPendingCount, setQuickActionPendingCount] = useState<number | null>(null);
  const [unreadNoticeCount, setUnreadNoticeCount] = useState<number | null>(null);
  const [approvalCenterPendingCount, setApprovalCenterPendingCount] = useState<number | null>(null);

  useEffect(() => {
    let stale = false;

    const loadTopups = async () => {
      try {
        const res = await listPositionTopups();
        if (stale) return;
        if (!res.ok) {
          setTopupInflightCount(null);
          return;
        }
        const n = (res.data.items ?? []).filter((t) => TOPUP_INFLIGHT.has(String(t.status).toUpperCase())).length;
        setTopupInflightCount(n);
      } catch {
        if (!stale) setTopupInflightCount(null);
      }
    };

    const loadQuickActions = async () => {
      try {
        const res = await listQuickActions('ALL');
        if (stale) return;
        if (!res.ok) {
          setQuickActionPendingCount(null);
          return;
        }
        // 历史审计记录 ≠ 待办；快捷处置页无待处理队列，角标恒为 0
        setQuickActionPendingCount(0);
      } catch {
        if (!stale) setQuickActionPendingCount(null);
      }
    };

    // 需求9 增量扩展：消息中心未读数（仅新增能力，不动既有徽章语义）
    const loadNotices = async () => {
      try {
        const res = await fetchNoticeUnreadCount();
        if (stale) return;
        // 失败 → null：侧栏角标隐藏数字，不影响菜单进入
        setUnreadNoticeCount(res.ok ? res.data.unreadCount : null);
      } catch {
        if (!stale) setUnreadNoticeCount(null);
      }
    };

    // 审批中心角标（PAGE-MCH-USER-015）：APC-002 动态计数；total 缺失按 byType 求和兜底；
    // 失败/403 → null 隐藏角标（不阻塞菜单进入）
    const loadApprovals = async () => {
      try {
        const res = await fetchPendingApprovalCount();
        if (stale) return;
        setApprovalCenterPendingCount(res.ok ? approvalBadgeCount(res.data) : null);
      } catch {
        if (!stale) setApprovalCenterPendingCount(null);
      }
    };

    void loadTopups();
    void loadNotices();
    void loadApprovals();
    // 无读权限不得预拉 listQuickActions，避免 dashboard 刷 403
    if (canReadQuickActions(merchantPermissionsOf(loadSession()))) {
      void loadQuickActions();
    }

    // 页内已读操作广播 → 即时重拉未读数（AC-07 本地即时刷新）
    const onNoticeChanged = () => void loadNotices();
    window.addEventListener(NOTICE_BADGE_EVENT, onNoticeChanged);
    // 审批中心决策成功 / 手动刷新广播 → 即时重拉 APC-002（AC-PAGE-MCH-USER-015-04）
    const onApprovalsChanged = () => void loadApprovals();
    window.addEventListener(APPROVALS_BADGE_EVENT, onApprovalsChanged);

    return () => {
      stale = true;
      window.removeEventListener(NOTICE_BADGE_EVENT, onNoticeChanged);
      window.removeEventListener(APPROVALS_BADGE_EVENT, onApprovalsChanged);
    };
  }, [pathname]);

  return { topupInflightCount, quickActionPendingCount, unreadNoticeCount, approvalCenterPendingCount };
}
